import { createSelector, createSelectorCreator, defaultMemoize } from 'reselect';
import { isEqual } from 'lodash';
import { findListerActivationBannerFromCatalogIds, getParentListerActivationBanner, getRootCatalogListerActivationBanners } from 'data/utils/banner';
import { getScreenUtmParams } from 'libs/common/event-tracker/helpers';
import { serverSide } from 'libs/utils/environment';
import { logWarning } from 'libs/utils/window';
import { getAnonId, getIsAuthenticated, getIsBusinessAccount, getLanguageCode, getTrackerDebugPin, getTrackingPlatform, getUserId } from './session/selectors';
import { getPortal as getRailsContextPortal } from './rails-context/selectors';
import { getScreenName } from './screen/selectors';
import { getPortal as getSystemConfigPortal } from './system-configuration/selectors';
import { getCatalogMap, getDeepestSelectedCatalog, getIsPersonalizationDisabled as getIsPersonalizationDisabledFilter, getQuery, getSelectedCatalogs, getSizeIds } from './catalog-filters/selectors';
import { getCatalogItemCurrentPage } from './items/selectors';
import { getBanners } from './banners/selectors';
import { getIsFeatureSwitchEnabled } from './feature-switches/selectors';
export const createDeepEqualSelector = createSelectorCreator(defaultMemoize, isEqual);
export const getIsPersonalizationDisabled = createSelector(getUserId, getQuery, getSizeIds, getCatalogItemCurrentPage, getIsPersonalizationDisabledFilter, (userId, query, sizeIds, page, isPersonalizationDisabled) => isPersonalizationDisabled || !userId || !!query || sizeIds.length > 0 || page && page > 1);
export const getEventTrackerContext = createSelector(getAnonId, getUserId, getRailsContextPortal, getSystemConfigPortal, getLanguageCode, getTrackerDebugPin, getTrackingPlatform, getScreenName, (anonId, userId, railsContextPortal, systemConfigPortal, languageCode, debugPin, platform, screenName) => {
  const utmParams = getScreenUtmParams();
  const portal = railsContextPortal || systemConfigPortal || '';
  if (!portal) logWarning('Missing portal for tracking.');
  return {
    anonId,
    userId,
    portal,
    languageCode,
    debugPin,
    platform,
    screen: {
      width: serverSide ? 0 : window.screen.width,
      height: serverSide ? 0 : window.screen.height,
      name: screenName
    },
    utm: {
      campaign: utmParams.utm_campaign,
      source: utmParams.utm_source,
      medium: utmParams.utm_medium,
      content: utmParams.utm_content
    }
  };
});
export const getAvailableListerBannersBySelectedCatalogs = createSelector(getSelectedCatalogs, getCatalogMap, getBanners, (selectedCatalogs, catalogMap, _ref) => {
  let {
    listerActivation
  } = _ref;
  if (!selectedCatalogs.length) return listerActivation;
  const selectedCatalogIds = selectedCatalogs.map(_ref2 => {
    let {
      id
    } = _ref2;
    return id;
  });
  const directBanner = findListerActivationBannerFromCatalogIds(selectedCatalogIds, listerActivation);
  const targetBanner = directBanner ? [directBanner] : undefined;
  return targetBanner || getParentListerActivationBanner(selectedCatalogs, catalogMap, listerActivation) || getRootCatalogListerActivationBanners(selectedCatalogs, listerActivation) || listerActivation;
});
export const getShowFundraiser = createSelector(getIsBusinessAccount, isBusinessAccount => !isBusinessAccount);
export const getIsReferralsEnabled = createSelector(getIsFeatureSwitchEnabled('payments'), getIsFeatureSwitchEnabled('referrals'), (isPaymentsEnabled, isReferralsEnabled) => isPaymentsEnabled && isReferralsEnabled);
export const getGoogleAnalyticsData = createSelector([getUserId, getIsAuthenticated, getAnonId, getLanguageCode, getDeepestSelectedCatalog, getScreenName], (userId, authStatus, anonymousId, language, currentCatalog, screenName) => {
  return {
    userId,
    authStatus,
    anonymousId,
    language,
    currentCatalogUrl: currentCatalog === null || currentCatalog === void 0 ? void 0 : currentCatalog.urlEn,
    screenName
  };
});